import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import classNames from 'classnames/bind';
import Scrollspy from 'react-scrollspy';
import _debounce from 'lodash/debounce';

// State
import { fetchPageIsNeeded, fetchCompositeDetailIsNeeded } from '../../state/modules/page/actions';

// Components
import Heading from '../../components/Heading';
import Container from '../../components/Container';
import InputField from '../../components/InputField';

// Icons
import SearchIcon from '../../icons/search.svg';

// Styles
import styles from './Page.styl';
import StyledHtml from '../../components/StyledHtml';

const cx = classNames.bind(styles);
export default function Page(props) {
  const { alias } = props;

  const dispatch = useDispatch();
  const { keywords: cityKeywords, compositePages = [] } = useSelector(state => state.city.userCity);
  const { isLoaded, list } = useSelector(state => state.page);
  const isRealPhone = useSelector(state => state.responsive.isRealPhone);
  const isRealTablet = useSelector(state => state.responsive.isRealTablet);
  const [searchValue, setSearchValue] = useState('');

  const isCompositePage = compositePages.includes(alias);
  const offset = isRealPhone || isRealTablet ? 0 : -85;
  const { title = '', body = '', sections = [], description = '', seo_title = '', keywords = '' } =
    list[alias] || {};
  const handleOnChange = e => {
    setSearchValue(e.target.value);
  };

  const handleClick = (e, anchor) => {
    e.preventDefault();

    const elem = document.querySelector(`#${anchor}`);

    if (elem !== null) {
      window.scrollBy({
        top: elem.getBoundingClientRect().top + offset,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (isCompositePage) {
      dispatch(fetchCompositeDetailIsNeeded({ alias }));
    } else {
      dispatch(fetchPageIsNeeded({ alias }));
    }
  }, []);

  const [top, setTop] = useState(223);

  const lastScrollPosition = useRef(0);
  const sidebarRef = useRef(null);

  const handleScroll = e => {
    if (!sidebarRef.current) {
      return;
    }

    const windowScrollY = window.scrollY;
    const windowHeight = e.target.documentElement.clientHeight;
    const sidebarObject = sidebarRef.current.getBoundingClientRect();
    const scrollDirectionUp = windowScrollY < lastScrollPosition.current;

    const scrollStep = Math.abs(windowScrollY - lastScrollPosition.current);
    const heightDiff = windowHeight - sidebarObject.height;

    let value = 100;

    // начальный Y сайдбара 80px
    // если мы долистали до сайдбара
    if (sidebarObject.y <= 223) {
      // если высота сайдбара больше чем (высота экрана - высота хэдера)
      if (heightDiff <= 223) {
        // если скроллим вверх
        if (scrollDirectionUp) {
          // если мы выше начальной позиции сайдбара, то ставим 80
          value = sidebarObject.y + scrollStep >= 223 ? 223 : sidebarObject.y + scrollStep;
          // если скроллим вниз
        } else {
          // умножаем на 0.8, чтобы на 0.8-х экрана торчал сайдбар
          value =
            sidebarObject.y - scrollStep <= windowHeight * 0.8 - sidebarObject.height
              ? windowHeight * 0.8 - sidebarObject.height
              : sidebarObject.y - scrollStep;
        }
      }

      setTop(value);
    }

    lastScrollPosition.current = windowScrollY;
  };

  const handleScrollDebounced = useCallback(
    _debounce(e => handleScroll(e), 250),
    [],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScrollDebounced, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollDebounced);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{seo_title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords || cityKeywords} />
      </Helmet>
      <Container className={cx('Page')} size={isCompositePage ? 'm' : 's'}>
        {isLoaded ? (
          <>
            <Heading level={1} className={cx('Page__title')}>
              {title}
            </Heading>
            {isCompositePage ? (
              <div className={cx('Page__composite')}>
                <div className={cx('Page__menu-container')}>
                  <div className={cx('Page__menu')} style={{ top: `${top}px` }} ref={sidebarRef}>
                    <div className={cx('Page__search')}>
                      <SearchIcon className={cx('Page__search-icon')} />
                      <InputField
                        className={cx('Page__search-input')}
                        name="search"
                        value={searchValue}
                        autoComplete="off"
                        placeholder="Поиск"
                        onChange={handleOnChange}
                      />
                    </div>
                    <Scrollspy
                      className={cx('Page__nav')}
                      currentClassName={cx('Page__nav-item_selected')}
                      items={sections.map(question => `${question.anchor}`)}
                      offset={offset}
                    >
                      {sections
                        .filter(question =>
                          question.name?.toLowerCase()?.includes(searchValue.toLowerCase()),
                        )
                        .map(question => (
                          <li
                            key={question.name}
                            className={cx('Page__nav-item')}
                            onClick={e => handleClick(e, question.anchor)}
                            onKeyDown={e => e.key === 'Enter' && handleClick(e, question.anchor)}
                            role="menuitem"
                          >
                            {question.name}
                          </li>
                        ))}
                    </Scrollspy>
                  </div>
                </div>
                <div className={cx('Page__list')}>
                  {sections.map(question => (
                    <section
                      key={question.anchor}
                      id={`${question.anchor}`}
                      className={cx('Page__list-item')}
                    >
                      <Heading className={cx('Page__list-item-title')} level={3}>
                        {question.name}
                      </Heading>
                      <div
                        className={cx('Page__list-item-description')}
                        dangerouslySetInnerHTML={{
                          __html: question.content,
                        }}
                      />
                    </section>
                  ))}
                </div>
              </div>
            ) : (
              <StyledHtml
                html={body}
                // className={cx('Page__content')}
              />
            )}
          </>
        ) : (
          <div className={cx('Page__spinner')} />
        )}
      </Container>
    </>
  );
}

Page.propTypes = {
  alias: PropTypes.string.isRequired,
};
